// Control de precios de compra: bandeja de alertas + variación semanal + tendencia.
// Inventario → Operaciones → Precios de compra.
// El reporte se DERIVA del kardex (no hay tabla de historial): nace con todo el pasado.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Settings, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { Campo, fieldCls, invRpc, money, num, partnersList, toNum } from './shared';
import type { PartnerOpt } from './shared';

type Tab = 'alertas' | 'variacion' | 'tendencia' | 'config';

interface Alerta {
  id: string; fecha: string; semaforo: string; estado: string;
  producto: string; codigo: string; volatilidad: string;
  proveedor: string | null; baseline_usado: string | null;
  costo_baseline: number | null; costo_nuevo: number; variacion_pct: number;
  cantidad: number; impacto_usd: number; motivo: string | null; comentario: string | null;
  documento: string | null; origen: string;
  resuelta_por: string | null; resuelta_at: string | null; precio_acordado: number | null;
}
interface Variacion {
  producto: string; codigo: string; volatilidad: string; proveedor: string | null; fecha: string;
  costo_actual: number; costo_anterior: number | null; promedio_ventana: number | null;
  precio_acordado: number | null; var_vs_anterior_pct: number | null;
  var_vs_promedio_pct: number | null; var_vs_acordado_pct: number | null;
  unidades: number; impacto_usd: number; semaforo: string | null; alerta_estado: string | null; motivo: string | null;
}
interface Tendencia {
  producto: string; codigo: string; proveedor: string | null; semanas_con_compra: number;
  costo_inicial: number; costo_final: number;
  semanas_al_alza: number; semanas_a_la_baja: number;
  deriva: 'sistematica' | 'oscilante' | 'estable';
  acumulado_pct: number | null; pendiente_pct_semana: number | null;
  proyeccion_anual_pct: number | null;
  serie: { semana: string; costo: number }[];
}
interface Config {
  baseline: string; ventana_n: number; tol_alta_pct: number; tol_media_pct: number;
  tol_baja_pct: number; tol_bajada_pct: number; materialidad_usd: number;
  tope_rojo_usd: number; proteger_costeo: boolean;
}

const MOTIVOS: { v: string; l: string }[] = [
  { v: 'escasez_clima', l: 'Escasez / clima' },
  { v: 'calibre', l: 'Cambió el calibre' },
  { v: 'cambio_calidad', l: 'Cambió la calidad' },
  { v: 'flete', l: 'Flete / transporte' },
  { v: 'error_lista', l: 'Error de lista del proveedor' },
  { v: 'otro', l: 'Otro' },
];

const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${Number(v) > 0 ? '▲' : Number(v) < 0 ? '▼' : ''} ${num(Math.abs(Number(v)), 1)}%`;

export function PreciosCompra() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('alertas');
  const [estadoAlerta, setEstadoAlerta] = useState('abierta');
  const [proveedor, setProveedor] = useState('');
  const [semanas, setSemanas] = useState('12');
  const [ordenTend, setOrdenTend] = useState('deriva');
  const [resolviendo, setResolviendo] = useState<Alerta | null>(null);
  const [motivo, setMotivo] = useState(MOTIVOS[0].v);
  const [comentario, setComentario] = useState('');
  const [fijar, setFijar] = useState(true);
  const [cfgEdit, setCfgEdit] = useState<Partial<Config>>({});

  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['inv-partners'], queryFn: partnersList });
  const proveedores = useMemo(
    () => partners.filter((p) => (p.tipo_partner ?? []).includes('proveedor')),
    [partners]);
  const { data: alertas = [] } = useQuery<Alerta[]>({
    queryKey: ['inv-precio-alertas', estadoAlerta],
    queryFn: () => invRpc<Alerta[]>('inv_precio_compra_alertas', { _estado: estadoAlerta }),
  });
  const { data: variacion = [] } = useQuery<Variacion[]>({
    queryKey: ['inv-precio-variacion', proveedor],
    queryFn: () => invRpc<Variacion[]>('inv_precio_compra_variacion', {
      _desde: null, _hasta: null, _partner: proveedor || null }),
    enabled: tab === 'variacion',
  });
  const { data: tendencia = [] } = useQuery<Tendencia[]>({
    queryKey: ['inv-precio-tendencia', semanas, proveedor, ordenTend],
    queryFn: () => invRpc<Tendencia[]>('inv_precio_compra_tendencia', {
      _semanas: toNum(semanas) || 12, _partner: proveedor || null, _orden: ordenTend }),
    enabled: tab === 'tendencia',
  });
  const { data: cfg } = useQuery<Config>({
    queryKey: ['inv-precio-config'],
    queryFn: () => invRpc<Config>('inv_precio_compra_config_get'),
  });

  const resolver = useMutation({
    mutationFn: (p: { id: string; accion: string }) => invRpc('inv_precio_compra_resolver', {
      _alerta_id: p.id, _accion: p.accion, _motivo: motivo, _comentario: comentario || null,
      _fijar_acordado: p.accion === 'aceptar' ? fijar : false }),
    onSuccess: () => {
      toast.success('Alerta resuelta');
      setResolviendo(null); setComentario('');
      void qc.invalidateQueries({ queryKey: ['inv-precio-alertas'] });
      void qc.invalidateQueries({ queryKey: ['inv-precio-variacion'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const guardarCfg = useMutation({
    mutationFn: () => invRpc('inv_precio_compra_config_set', { _payload: { ...cfg, ...cfgEdit } }),
    onSuccess: () => { toast.success('Control de precios actualizado'); setCfgEdit({}); void qc.invalidateQueries({ queryKey: ['inv-precio-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abiertas = useMemo(() => alertas.filter((a) => a.estado === 'abierta').length, [alertas]);
  const impactoAbierto = useMemo(
    () => alertas.filter((a) => a.estado === 'abierta').reduce((s, a) => s + Number(a.impacto_usd || 0), 0),
    [alertas]);

  const tabs: [Tab, string][] = [
    ['alertas', `Alertas${abiertas ? ` (${abiertas})` : ''}`],
    ['variacion', 'Variación por proveedor'],
    ['tendencia', 'Tendencia'],
    ['config', 'Umbrales'],
  ];

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <TrendingUp className="w-5 h-5" /> Control de precios de compra
      </h1>

      <div className="flex flex-wrap items-center gap-2 border-b">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'alertas' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Desviaciones de precio
              {abiertas > 0 && <span className="text-sm font-normal text-muted-foreground">
                · {abiertas} sin resolver por {money(impactoAbierto)}</span>}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              La mercadería ya entró (nunca se bloquea el descargue). Mientras la desviación no se apruebe,
              el costo con que se costean las recetas <b>no se actualiza</b>.
            </p>
            <div className="pt-2">
              <select className={`${fieldCls} !w-auto`} value={estadoAlerta} onChange={(e) => setEstadoAlerta(e.target.value)}>
                <option value="abierta">Sin resolver</option>
                <option value="aceptada">Aceptadas</option>
                <option value="rechazada">Rechazadas</option>
                <option value="todas">Todas</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2">N°</th><th className="py-2 pr-2">Fecha</th><th className="py-2 pr-2">Producto</th>
                <th className="py-2 pr-2">Proveedor</th><th className="py-2 pr-2 text-right">Baseline</th>
                <th className="py-2 pr-2 text-right">Pagado</th><th className="py-2 pr-2 text-right">Δ%</th>
                <th className="py-2 pr-2 text-right">Impacto</th><th className="py-2 pr-2">Estado</th><th />
              </tr></thead>
              <tbody>
                {alertas.map((a, i) => (
                  <tr key={a.id} className={`border-b last:border-0 ${a.estado === 'abierta'
                    ? a.semaforo === 'rojo' ? 'bg-red-50 dark:bg-red-900/10' : 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                    <td className="py-2 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-2">{a.fecha}</td>
                    <td className="py-2 pr-2">
                      {a.producto}
                      <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">{a.volatilidad}</span>
                      {a.documento && <span className="block text-xs text-muted-foreground">{a.documento}</span>}
                    </td>
                    <td className="py-2 pr-2 text-xs">{a.proveedor ?? '—'}</td>
                    <td className="py-2 pr-2 text-right font-mono">
                      {money(a.costo_baseline ?? 0)}
                      <span className="block text-[10px] text-muted-foreground">{a.baseline_usado}</span>
                    </td>
                    <td className="py-2 pr-2 text-right font-mono">{money(a.costo_nuevo)}</td>
                    <td className={`py-2 pr-2 text-right font-mono font-semibold ${a.semaforo === 'rojo' ? 'text-red-600' : 'text-amber-600'}`}>
                      {pct(a.variacion_pct)}
                    </td>
                    <td className="py-2 pr-2 text-right font-mono">{money(a.impacto_usd)}</td>
                    <td className="py-2 pr-2 text-xs">
                      {a.estado === 'abierta' ? <span className="font-medium">sin resolver</span> : (
                        <>
                          {a.estado}
                          {a.motivo && <span className="block text-muted-foreground">{MOTIVOS.find((m) => m.v === a.motivo)?.l ?? a.motivo}</span>}
                          {a.resuelta_por && <span className="block text-muted-foreground">por {a.resuelta_por}</span>}
                        </>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {a.estado === 'abierta' && (
                        <Button size="sm" variant="outline" onClick={() => { setResolviendo(a); setMotivo(MOTIVOS[0].v); setComentario(''); setFijar(true); }}>
                          Resolver
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!alertas.length && (
                  <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">
                    Sin alertas — todos los precios llegaron dentro de lo acordado.
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === 'variacion' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Variación por proveedor y producto</CardTitle>
            <p className="text-sm text-muted-foreground">
              Ordenada por <b>impacto en dólares</b>, no por porcentaje: arriba están los ítems que mueven la plata.
            </p>
            <div className="pt-2">
              <select className={`${fieldCls} !w-auto`} value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
                <option value="">Todos los proveedores</option>
                {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
              </select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2">N°</th><th className="py-2 pr-2">Producto</th><th className="py-2 pr-2">Proveedor</th>
                <th className="py-2 pr-2">Última compra</th><th className="py-2 pr-2 text-right">Actual</th>
                <th className="py-2 pr-2 text-right">Anterior</th><th className="py-2 pr-2 text-right">Δ% ant.</th>
                <th className="py-2 pr-2 text-right">Prom.</th><th className="py-2 pr-2 text-right">Δ% prom.</th>
                <th className="py-2 pr-2 text-right">Acordado</th><th className="py-2 pr-2 text-right">Δ% acord.</th>
                <th className="py-2 pr-2 text-right">Impacto</th>
              </tr></thead>
              <tbody>
                {variacion.map((v, i) => (
                  <tr key={`${v.codigo}-${v.proveedor}-${i}`} className={`border-b last:border-0 ${
                    v.semaforo === 'rojo' ? 'bg-red-50 dark:bg-red-900/10' : v.semaforo === 'ambar' ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                    <td className="py-2 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-2">{v.producto}</td>
                    <td className="py-2 pr-2 text-xs">{v.proveedor ?? '—'}</td>
                    <td className="py-2 pr-2 text-xs">{v.fecha}</td>
                    <td className="py-2 pr-2 text-right font-mono">{money(v.costo_actual)}</td>
                    <td className="py-2 pr-2 text-right font-mono text-muted-foreground">{v.costo_anterior != null ? money(v.costo_anterior) : '—'}</td>
                    <td className="py-2 pr-2 text-right font-mono">{pct(v.var_vs_anterior_pct)}</td>
                    <td className="py-2 pr-2 text-right font-mono text-muted-foreground">{v.promedio_ventana != null ? money(v.promedio_ventana) : '—'}</td>
                    <td className="py-2 pr-2 text-right font-mono">{pct(v.var_vs_promedio_pct)}</td>
                    <td className="py-2 pr-2 text-right font-mono text-muted-foreground">{v.precio_acordado != null ? money(v.precio_acordado) : '—'}</td>
                    <td className="py-2 pr-2 text-right font-mono font-semibold">{pct(v.var_vs_acordado_pct)}</td>
                    <td className="py-2 pr-2 text-right font-mono">{money(v.impacto_usd)}</td>
                  </tr>
                ))}
                {!variacion.length && <tr><td colSpan={12} className="py-8 text-center text-muted-foreground">Sin compras registradas.</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === 'tendencia' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Tendencia por semanas</CardTitle>
            <p className="text-sm text-muted-foreground">
              Distingue lo <b>negociable</b> de lo que no lo es: un precio que <b>sube siempre</b> es deriva del
              proveedor (aunque el acumulado sea chico); uno que sube y baja es el mercado respirando.
              Ordenado por deriva, las alzas sistemáticas salen primero.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              <select className={`${fieldCls} !w-auto`} value={ordenTend} onChange={(e) => setOrdenTend(e.target.value)}>
                <option value="deriva">Deriva sistemática primero (negociable)</option>
                <option value="acumulado">Mayor variación acumulada (picos de mercado)</option>
              </select>
              <select className={`${fieldCls} !w-auto`} value={semanas} onChange={(e) => setSemanas(e.target.value)}>
                {['8', '12', '26', '52'].map((s) => <option key={s} value={s}>{s} semanas</option>)}
              </select>
              <select className={`${fieldCls} !w-auto`} value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
                <option value="">Todos los proveedores</option>
                {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
              </select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2">N°</th><th className="py-2 pr-2">Producto</th><th className="py-2 pr-2">Proveedor</th>
                <th className="py-2 pr-2">Deriva</th><th className="py-2 pr-2 text-right">Sube/baja</th>
                <th className="py-2 pr-2 text-right">Inicial</th>
                <th className="py-2 pr-2 text-right">Final</th><th className="py-2 pr-2 text-right">Acumulado</th>
                <th className="py-2 pr-2 text-right">Por semana</th><th className="py-2 pr-2 text-right">Si sigue así (año)</th>
                <th className="py-2 pr-2">Serie</th>
              </tr></thead>
              <tbody>
                {tendencia.map((t, i) => (
                  <tr key={`${t.codigo}-${i}`} className={`border-b last:border-0 ${
                    t.deriva === 'sistematica' ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                    <td className="py-2 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-2">{t.producto}</td>
                    <td className="py-2 pr-2 text-xs">{t.proveedor ?? '—'}</td>
                    <td className="py-2 pr-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        t.deriva === 'sistematica' ? 'bg-amber-200 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200'
                        : t.deriva === 'oscilante' ? 'bg-muted text-muted-foreground' : 'bg-muted text-muted-foreground'}`}>
                        {t.deriva === 'sistematica' ? 'sube siempre' : t.deriva === 'oscilante' ? 'oscila' : 'estable'}
                      </span>
                    </td>
                    <td className="py-2 pr-2 text-right font-mono text-xs">{t.semanas_al_alza}/{t.semanas_a_la_baja}</td>
                    <td className="py-2 pr-2 text-right font-mono">{money(t.costo_inicial)}</td>
                    <td className="py-2 pr-2 text-right font-mono">{money(t.costo_final)}</td>
                    <td className="py-2 pr-2 text-right font-mono font-semibold">{pct(t.acumulado_pct)}</td>
                    <td className="py-2 pr-2 text-right font-mono text-muted-foreground">{pct(t.pendiente_pct_semana)}</td>
                    <td className={`py-2 pr-2 text-right font-mono ${t.deriva === 'sistematica' ? 'font-semibold text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>
                      {t.proyeccion_anual_pct != null ? `${num(t.proyeccion_anual_pct, 0)}%` : '—'}
                    </td>
                    <td className="py-2 pr-2">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {t.serie.map((s) => num(s.costo, 2)).join(' → ')}
                      </span>
                    </td>
                  </tr>
                ))}
                {!tendencia.length && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">
                  Se necesitan al menos 2 compras del mismo ítem al mismo proveedor en el período.</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === 'config' && cfg && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Settings className="w-4 h-4" /> Umbrales del control</CardTitle>
            <p className="text-sm text-muted-foreground">
              Los umbrales van por <b>clase de volatilidad del producto</b> (se marca en la ficha de cada producto):
              en perecibles un ±20% semanal es el mercado respirando; en abarrote no.
            </p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Campo label="Comparar contra">
              <select className={fieldCls} value={String(cfgEdit.baseline ?? cfg.baseline)}
                onChange={(e) => setCfgEdit((s) => ({ ...s, baseline: e.target.value }))}>
                <option value="acordado">Precio acordado (recomendado)</option>
                <option value="promedio_n">Promedio de las últimas N compras</option>
                <option value="ultimo">Último precio pagado (esquivable)</option>
              </select>
            </Campo>
            {([
              ['ventana_n', 'Ventana (compras/semanas)'],
              ['tol_alta_pct', 'Tolerancia volatilidad ALTA %'],
              ['tol_media_pct', 'Tolerancia MEDIA %'],
              ['tol_baja_pct', 'Tolerancia BAJA %'],
              ['tol_bajada_pct', 'Tolerancia de BAJADA %'],
              ['materialidad_usd', 'No alertar si el impacto es menor a $'],
              ['tope_rojo_usd', 'Rojo si el impacto supera $'],
            ] as [keyof Config, string][]).map(([k, label]) => (
              <Campo key={k} label={label}>
                <input className={fieldCls} value={String(cfgEdit[k] ?? cfg[k] ?? '')}
                  onChange={(e) => setCfgEdit((s) => ({ ...s, [k]: toNum(e.target.value) }))} />
              </Campo>
            ))}
            <Campo label="Proteger el costeo de recetas">
              <select className={fieldCls} value={String(cfgEdit.proteger_costeo ?? cfg.proteger_costeo)}
                onChange={(e) => setCfgEdit((s) => ({ ...s, proteger_costeo: e.target.value === 'true' }))}>
                <option value="true">Sí — un precio no aprobado no cambia el costo de referencia</option>
                <option value="false">No — el costo se actualiza siempre</option>
              </select>
            </Campo>
            <div className="md:col-span-3">
              <Button disabled={!Object.keys(cfgEdit).length || guardarCfg.isPending} onClick={() => guardarCfg.mutate()}>
                Guardar umbrales
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resolver una alerta */}
      {resolviendo && (
        <PanelIntegrado title="Resolver desviación de precio" onClose={() => setResolviendo(null)}>
          <p className="-mt-1 pb-2 text-xs text-muted-foreground">
            {resolviendo.producto} · {resolviendo.proveedor} · {money(resolviendo.costo_baseline ?? 0)} → {money(resolviendo.costo_nuevo)}
            {' '}({pct(resolviendo.variacion_pct)}) · impacto {money(resolviendo.impacto_usd)}
          </p>
          <div className="space-y-3">
            <div>
              <p className="pb-1 text-sm font-medium">Motivo</p>
              <div className="grid grid-cols-2 gap-2">
                {MOTIVOS.map((m) => (
                  <button key={m.v} onClick={() => setMotivo(m.v)}
                    className={`rounded-md border-2 px-2 py-2 text-xs font-medium ${motivo === m.v ? 'border-primary bg-accent' : ''}`}>
                    {m.l}
                  </button>
                ))}
              </div>
            </div>
            <input className={fieldCls} placeholder="Comentario (opcional)" value={comentario} onChange={(e) => setComentario(e.target.value)} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={fijar} onChange={(e) => setFijar(e.target.checked)} />
              Fijar este precio como el nuevo <b>acordado</b> (así deja de alertar en la próxima compra)
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setResolviendo(null)}>Cancelar</Button>
              <Button variant="outline" disabled={resolver.isPending}
                onClick={() => resolver.mutate({ id: resolviendo.id, accion: 'rechazar' })}>
                <X className="mr-1 h-4 w-4" /> Rechazar (reclamar al proveedor)
              </Button>
              <Button disabled={resolver.isPending}
                onClick={() => resolver.mutate({ id: resolviendo.id, accion: 'aceptar' })}>
                <Check className="mr-1 h-4 w-4" /> Aceptar el alza
              </Button>
            </div>
          </div>
        </PanelIntegrado>
      )}
    </div>
  );
}
